import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * TimePickerSpinner — custom HH:MM picker for DateTimePicker.
 *
 * Replaces the native <input type="time"> (recurring "flashazo" regression —
 * Altiv never uses native HTML date/time inputs). Two independent spinners
 * for hours (00–23) and minutes (00–59), with chevron steppers + direct
 * keyboard entry through styled number inputs.
 *
 * Out-of-range values from typed input are clamped silently (never written
 * to state). Empty input collapses to "00".
 *
 * @param {Object} props
 * @param {string} props.value  "HH:MM" 24h, e.g. "14:30". Empty defaults to "00:00".
 * @param {(next: string) => void} props.onChange  Fires only with valid in-range "HH:MM".
 */
export default function TimePickerSpinner({ value, onChange }) {
  const { t } = useTranslation();

  const [hours, minutes] = React.useMemo(() => {
    const raw = (value || "00:00").split(":");
    const h = clampInt(raw[0], 0, 23, 0);
    const m = clampInt(raw[1], 0, 59, 0);
    return [h, m];
  }, [value]);

  const emit = React.useCallback(
    (h, m) => {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      onChange(`${hh}:${mm}`);
    },
    [onChange]
  );

  const bumpHours = (delta) => emit((hours + delta + 24) % 24, minutes);
  const bumpMinutes = (delta) => emit(hours, (minutes + delta + 60) % 60);

  return (
    <div className="flex items-center justify-center gap-2" data-testid="time-picker-spinner">
      <SpinnerColumn
        label={t("dateTimePicker.hours")}
        value={hours}
        max={23}
        onChange={(h) => emit(h, minutes)}
        onIncrement={() => bumpHours(1)}
        onDecrement={() => bumpHours(-1)}
        testId="time-picker-hours"
      />
      <span className="text-lg font-mono text-slate-400 pt-5" aria-hidden="true">
        :
      </span>
      <SpinnerColumn
        label={t("dateTimePicker.minutes")}
        value={minutes}
        max={59}
        onChange={(m) => emit(hours, m)}
        onIncrement={() => bumpMinutes(1)}
        onDecrement={() => bumpMinutes(-1)}
        testId="time-picker-minutes"
      />
    </div>
  );
}

function SpinnerColumn({ label, value, max, onChange, onIncrement, onDecrement, testId }) {
  // Use a string buffer so the user can clear the field while typing
  // without us snapping back to "00" on every keystroke.
  const [buffer, setBuffer] = React.useState(String(value).padStart(2, "0"));
  // Re-sync buffer when the parent-driven `value` changes (e.g. chevron click).
  React.useEffect(() => {
    setBuffer(String(value).padStart(2, "0"));
  }, [value]);

  const commit = (raw) => {
    if (raw === "") {
      onChange(0);
      return;
    }
    const n = clampInt(raw, 0, max, null);
    if (n === null) {
      // Reject out-of-range — restore last good value.
      setBuffer(String(value).padStart(2, "0"));
      return;
    }
    onChange(n);
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[0.65rem] uppercase tracking-wide text-slate-500">{label}</span>
      <button
        type="button"
        onClick={onIncrement}
        aria-label={`${label} +1`}
        className={cn(
          "h-5 w-12 flex items-center justify-center rounded-md",
          "text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
        )}
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={buffer}
        data-testid={testId}
        onChange={(e) => {
          // Strip non-digits while typing; commit on blur.
          const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
          setBuffer(digits);
        }}
        onBlur={() => commit(buffer)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            onIncrement();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            onDecrement();
          }
        }}
        className={cn(
          "w-12 bg-[#0f172a] border border-slate-700 rounded-lg px-1 py-1.5",
          "text-sm text-white font-mono text-center tabular-nums",
          "focus:border-[#0052FF] focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
        )}
      />
      <button
        type="button"
        onClick={onDecrement}
        aria-label={`${label} -1`}
        className={cn(
          "h-5 w-12 flex items-center justify-center rounded-md",
          "text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
        )}
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Parse + clamp to [min,max]. Returns `fallback` if input is not a valid int in range. */
function clampInt(raw, min, max, fallback) {
  if (raw === null || raw === undefined || raw === "") return fallback;
  const n = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(n)) return fallback;
  if (n < min || n > max) return fallback;
  return n;
}
